import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { ThemeProvider } from 'styled-components';
import type {
  EffectsFunction,
  TrackEffectsFunction,
  SoundFontCache,
} from '@waveform-playlist/playout';
import { resolvePlayoutAdapter } from './playout/resolvePlayoutAdapter';
import { PlaylistEngine, type EngineState, type PlayoutAdapter } from '@waveform-playlist/engine';
import {
  type ClipTrack,
  type Fade,
  type AnnotationAction,
  type MidiNoteData,
} from '@waveform-playlist/core';
import {
  type TimeFormat,
  type WaveformPlaylistTheme,
  defaultTheme,
} from '@waveform-playlist/ui-components';
import { extractPeaksFromWaveformDataFull } from './waveformDataLoader';
import { syncSoundFontCacheToAdapter } from './soundFontSync';
import type { TrackDragPreview } from './utils/trackOrderPreview';
import type WaveformData from 'waveform-data';
import type { PeakData } from '@waveform-playlist/core';
import type { AnnotationData } from '@waveform-playlist/core';
import {
  useTimeFormat,
  useZoomControls,
  useMasterVolume,
  useSelectionState,
  useLoopState,
  useSelectedTrack,
  useUndoState,
  useAnimationFrameLoop,
  useWaveformDataCache,
} from './hooks';

// Types
export interface ClipPeaks {
  clipId: string;
  trackName: string;
  peaks: PeakData;
  startSample: number;
  durationSamples: number;
  fadeIn?: Fade;
  fadeOut?: Fade;
  midiNotes?: MidiNoteData[];
  sampleRate?: number;
  offsetSamples?: number;
}

export type TrackClipPeaks = ClipPeaks[];

export type { TrackDragPreview };

// Legacy WaveformTrack type - kept for reference but deprecated
// @deprecated Use ClipTrack from @waveform-playlist/core instead
export interface WaveformTrack {
  src: string | AudioBuffer;
  name?: string;
  effects?: TrackEffectsFunction;
}

export interface TrackState {
  name: string;
  muted: boolean;
  soloed: boolean;
  volume: number;
  pan: number;
}

// Split contexts for performance optimization
// Animation context contains playback state and timing refs — no per-frame state updates

/** Per-frame data passed to registered animation callbacks. */
export interface FrameData {
  /** Raw engine time (for state/logic — NOT for visual positioning). */
  readonly time: number;
  /** Visually-aligned time for DOM positioning: engine.getAudibleTime() while
   *  playing (matches speaker output), raw time when resting. */
  readonly visualTime: number;
  readonly sampleRate: number;
  readonly samplesPerPixel: number;
}

export interface PlaybackAnimationContextValue {
  isPlaying: boolean;
  currentTime: number;
  currentTimeRef: React.RefObject<number>;
  /**
   * Visually-aligned playback time (engine.getAudibleTime() while playing;
   * raw resting time otherwise). Kept current by the animation loop during playback
   * and by pause/seek/stop paths when stopped. Read from this for any visual
   * positioning that should match the audible output.
   */
  visualTimeRef: React.RefObject<number>;
  // Refs for direct time calculation in animated components (avoids timing drift)
  playbackStartTimeRef: React.RefObject<number>; // context.currentTime when playback started
  audioStartPositionRef: React.RefObject<number>; // Audio position when playback started
  /** Returns raw playback time from engine (auto-wraps at loop boundaries). */
  getPlaybackTime: () => number;
  /** Current time of the adapter's AudioContext, in seconds. */
  getAudioContextTime: () => number;
  /**
   * Returns the current adapter scheduler lookahead (Tone ~0.1s, native 0).
   * Use this for any audible-latency calculation that must match the playhead
   * (e.g., recording live-preview peak slicing).
   */
  getLookAhead: () => number;
  /**
   * Returns the adapter AudioContext's output latency in seconds.
   * Use this for audible-latency calculations (e.g., recording live-preview peak slicing).
   */
  getOutputLatency: () => number;
  /** Register a per-frame callback driven by the single animation loop. */
  registerFrameCallback: (id: string, cb: (data: FrameData) => void) => void;
  /** Unregister a per-frame callback. */
  unregisterFrameCallback: (id: string) => void;
}

export interface PlaylistStateContextValue {
  continuousPlay: boolean;
  linkEndpoints: boolean;
  annotationsEditable: boolean;
  isAutomaticScroll: boolean;
  isLoopEnabled: boolean;
  annotations: AnnotationData[];
  activeAnnotationId: string | null;
  selectionStart: number;
  selectionEnd: number;
  selectedTrackId: string | null; // ID of currently selected track for editing operations
  // Loop region (separate from selection) - Audacity-style loop points
  loopStart: number;
  loopEnd: number;
  /** Whether playback continues past the end of loaded audio instead of
   *  auto-stopping (implies the fillViewport layout) */
  indefinitePlayback: boolean;
  /** Whether the timeline visually fills the scroll container even when the
   *  audio is shorter (layout only — no effect on playback) */
  fillViewport: boolean;
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
}

export interface PlaylistControlsContextValue {
  // Playback controls
  play: (startTime?: number, playDuration?: number) => Promise<void>;
  pause: () => void;
  stop: () => void;
  seekTo: (time: number) => void;
  setCurrentTime: (time: number) => void;

  // Track controls
  setTrackMute: (trackIndex: number, muted: boolean) => void;
  setTrackSolo: (trackIndex: number, soloed: boolean) => void;
  setTrackVolume: (trackIndex: number, volume: number) => void;
  setTrackPan: (trackIndex: number, pan: number) => void;

  /** Move a track to a new index in the vertical track order. Purely
   *  organizational — playback is not interrupted. The reordered tracks
   *  array flows back through onTracksChange. */
  reorderTrack: (trackId: string, toIndex: number) => void;

  /** Internal: call after every track-reorder DRAG interaction ends
   *  (committed or canceled) to force PlaylistVisualization to remount the
   *  track-controls slots, discarding any @dnd-kit sortable-plugin DOM
   *  corruption. Not needed for button-driven reorderTrack calls (they never
   *  touch dnd-kit's DOM). See bumpTrackReorderEpoch in the provider. */
  bumpTrackReorderEpoch: () => void;

  /** INTERNAL wiring for ClipInteractionProvider: publishes/clears the live
   *  track-reorder drag preview. Custom drag integrations may call it, but
   *  most consumers only ever READ trackDragPreview from usePlaylistData(). */
  setTrackDragPreview: React.Dispatch<React.SetStateAction<TrackDragPreview | null>>;

  // Selection
  setSelection: (start: number, end: number) => void;
  setSelectedTrackId: (trackId: string | null) => void;

  // Time format
  setTimeFormat: (format: TimeFormat) => void;
  formatTime: (seconds: number) => string;

  // Zoom
  zoomIn: () => void;
  zoomOut: () => void;

  // Master volume
  setMasterVolume: (volume: number) => void;

  // Automatic scroll
  setAutomaticScroll: (enabled: boolean) => void;
  setScrollContainer: (element: HTMLDivElement | null) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;

  // Annotation controls
  setContinuousPlay: (enabled: boolean) => void;
  setLinkEndpoints: (enabled: boolean) => void;
  setAnnotationsEditable: (enabled: boolean) => void;
  setAnnotations: React.Dispatch<React.SetStateAction<AnnotationData[]>>;
  setActiveAnnotationId: (id: string | null) => void;

  // Loop controls
  setLoopEnabled: (enabled: boolean) => void;
  setLoopRegion: (start: number, end: number) => void;
  setLoopRegionFromSelection: () => void;
  clearLoopRegion: () => void;

  // Undo/redo
  undo: () => void;
  redo: () => void;

  // Recording
  /** Mark a recording session active/inactive. While active: (1) the
   *  end-of-audio auto-stop is suppressed so overdub playback runs past the
   *  end of existing material, and (2) with an `armedTrackId`, that track's
   *  existing content is transiently muted — punch-in recording replaces
   *  whatever the take overlaps (#579), so the doomed material must not play
   *  under the overdub; its previous mute state is restored when the session
   *  ends (audio-only, the UI mute control is untouched). Wired automatically
   *  from the Waveform / PlaylistVisualization `recordingState` prop; overdub
   *  flows should also call it eagerly (before `play()`). */
  setRecordingActive: (active: boolean, armedTrackId?: string | null) => void;
}

export interface PlaylistDataContextValue {
  duration: number;
  audioBuffers: AudioBuffer[];
  peaksDataArray: TrackClipPeaks[]; // Array of tracks, each containing array of clip peaks
  trackStates: TrackState[];
  tracks: ClipTrack[]; // Original tracks array with IDs
  sampleRate: number;
  waveHeight: number;
  timeScaleHeight: number;
  minimumPlaylistHeight: number;
  controls: { show: boolean; width: number };
  playoutRef: React.RefObject<PlaylistEngine | null>;
  samplesPerPixel: number;
  timeFormat: TimeFormat;
  masterVolume: number;
  canZoomIn: boolean;
  canZoomOut: boolean;
  barWidth: number;
  barGap: number;
  /** Draw bars with pill-shaped rounded caps (radius barWidth/2). */
  roundedBars: boolean;
  /** Width in pixels of progress bars. Defaults to barWidth + barGap (fills gaps). */
  progressBarWidth: number;
  /** Whether the playlist has finished loading all tracks */
  isReady: boolean;
  /** Internal: incremented after every track-reorder drag interaction ends.
   *  PlaylistVisualization folds this into the track-controls slot `key` to
   *  force a remount, working around @dnd-kit sortable-plugin DOM corruption
   *  (see bumpTrackReorderEpoch in PlaylistControlsContextValue). */
  trackReorderEpoch: number;
  /** Non-null while a track-reorder drag is live: the dragged track and the
   *  index it would drop at. Both playlist columns derive their display
   *  layout from it; custom renderTrackControls consumers can read it to
   *  mirror the preview in their own UI. */
  trackDragPreview: TrackDragPreview | null;
  /** Whether tracks are rendered in mono mode */
  mono: boolean;
  /** Ref set by useClipDragHandlers during boundary trim drags.
   *  When true, loadAudio skips engine rebuild — visual updates flow via React state only. */
  isDraggingRef: React.MutableRefObject<boolean>;
  onTracksChange: ((tracks: ClipTrack[]) => void) | undefined;
}

// Create the 4 separate contexts
const PlaybackAnimationContext = createContext<PlaybackAnimationContextValue | null>(null);
const PlaylistStateContext = createContext<PlaylistStateContextValue | null>(null);
const PlaylistControlsContext = createContext<PlaylistControlsContextValue | null>(null);
const PlaylistDataContext = createContext<PlaylistDataContextValue | null>(null);

export interface WaveformPlaylistProviderProps {
  tracks: ClipTrack[]; // Updated to use clip-based model
  timescale?: boolean;
  mono?: boolean;
  waveHeight?: number;
  samplesPerPixel?: number;
  zoomLevels?: number[]; // Array of zoom levels in samples per pixel (lower = more zoomed in)
  automaticScroll?: boolean;
  theme?: Partial<WaveformPlaylistTheme>;
  controls?: {
    show: boolean;
    width: number;
  };
  annotationList?: {
    annotations?: AnnotationData[];
    editable?: boolean;
    isContinuousPlay?: boolean;
    linkEndpoints?: boolean;
    controls?: AnnotationAction[];
  };
  effects?: EffectsFunction;
  onReady?: () => void;
  /** Called when audio/engine initialization fails (e.g. a missing optional peer,
   *  a throwing `createAdapter`, or an invalid `zoomLevels`/`samplesPerPixel`). The
   *  provider already logs the error; use this to surface it in your UI. */
  onError?: (err: Error) => void;
  /** @deprecated Use onAnnotationsChange instead */
  onAnnotationUpdate?: (annotations: AnnotationData[]) => void;
  /** Callback when annotations are changed (drag, edit, etc.) */
  onAnnotationsChange?: (annotations: AnnotationData[]) => void;
  /** Width in pixels of waveform bars. Default: 1 */
  barWidth?: number;
  /** Spacing in pixels between waveform bars. Default: 0 */
  barGap?: number;
  /** Draw bars with pill-shaped rounded caps (radius barWidth/2). Default: false */
  roundedBars?: boolean;
  /** Width in pixels of progress bars. Default: barWidth + barGap (fills gaps). */
  progressBarWidth?: number;
  /** Callback when engine clip operations (move, trim, split) change tracks.
   * The provider calls this so the parent can update its tracks state without
   * triggering a full engine rebuild.
   *
   * **Important:** The parent must pass the received `tracks` reference back as
   * the `tracks` prop (i.e. `setState(tracks)`). The provider uses reference
   * identity (`tracks === engineTracksRef.current`) to detect engine-originated
   * updates and skip the expensive `loadAudio` rebuild. */
  onTracksChange?: (tracks: ClipTrack[]) => void;
  /** SoundFont cache for sample-based MIDI playback. When provided, MIDI clips
   *  use SoundFont samples instead of PolySynth synthesis. */
  soundFontCache?: SoundFontCache;
  /** When true, tracks render visually but the engine build is deferred.
   *  Use this during progressive loading to avoid rebuilding the engine for
   *  each track — flip to false when all tracks are ready for a single build. */
  deferEngineRebuild?: boolean;
  /** Disable the automatic stop when the cursor reaches the end of the
   *  longest track — the transport rolls until an explicit stop/pause
   *  (DAW-style). Implies the fillViewport layout. Recording sessions
   *  suppress the auto-stop automatically, so most recording UIs don't
   *  need this. */
  indefinitePlayback?: boolean;
  /** Extend the timeline (background + timescale) to fill the visible scroll
   *  container even when the audio is shorter. Layout only. Recording UIs
   *  typically want this so the empty timeline doesn't collapse to the audio
   *  width. */
  fillViewport?: boolean;
  /** Desired AudioContext sample rate. Creates a cross-browser AudioContext at
   *  this rate via standardized-audio-context. Pre-computed peaks (.dat files)
   *  render instantly when they match. On mismatch, falls back to worker. */
  sampleRate?: number;
  /** Factory for a custom PlayoutAdapter. When omitted, the Tone.js engine
   *  (@waveform-playlist/playout) is dynamically imported — so a custom adapter
   *  lets a consumer use neither @waveform-playlist/playout nor tone. Called once
   *  per engine rebuild; the provider owns and disposes the returned instance.
   *  Pass a stable reference (module-level or useCallback) — it is read directly
   *  inside the curated `loadAudio` effect, not via its dependency array. */
  createAdapter?: () => PlayoutAdapter;
  children: ReactNode;
}

export const WaveformPlaylistProvider: React.FC<WaveformPlaylistProviderProps> = ({
  tracks,
  timescale = false,
  mono = false,
  waveHeight = 80,
  samplesPerPixel: initialSamplesPerPixel = 1024,
  zoomLevels,
  automaticScroll = false,
  theme: userTheme,
  controls = { show: false, width: 0 },
  annotationList,
  effects,
  onReady,
  onError,
  onAnnotationUpdate: _onAnnotationUpdate,
  onAnnotationsChange,
  barWidth = 1,
  barGap = 0,
  roundedBars = false,
  progressBarWidth: progressBarWidthProp,
  onTracksChange,
  soundFontCache,
  deferEngineRebuild = false,
  indefinitePlayback = false,
  fillViewport = false,
  sampleRate: sampleRateProp,
  createAdapter,
  children,
}) => {
  // Default progressBarWidth to barWidth + barGap (fills gaps)
  const progressBarWidth = progressBarWidthProp ?? barWidth + barGap;

  // Stabilize zoomLevels reference — inline arrays (e.g. zoomLevels={[256, 512]})
  // create a new reference every render, which would trigger engine rebuild via
  // loadAudio deps. Content-based comparison avoids spurious rebuilds.
  const stableZoomLevels = useMemo(
    () => zoomLevels,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zoomLevels?.join(',')]
  );

  // Annotations are derived from prop (single source of truth in parent)
  // In v6, annotations must be pre-parsed (numeric start/end). Use parseAeneas() from @waveform-playlist/annotations before passing.
  const annotations = useMemo(() => {
    if (!annotationList?.annotations) return [];
    if (process.env.NODE_ENV !== 'production' && annotationList.annotations.length > 0) {
      const first = annotationList.annotations[0] as unknown as Record<string, unknown>;
      if (typeof first.start !== 'number' || typeof first.end !== 'number') {
        console.error(
          '[waveform-playlist] Annotations must have numeric start/end values. ' +
            'In v6, use parseAeneas() from @waveform-playlist/annotations before passing annotations. ' +
            'Received start type: ' +
            typeof first.start
        );
        return [];
      }
    }
    return annotationList.annotations;
  }, [annotationList?.annotations]);

  // Ref for animation loop (avoids restarting loop on annotation change)
  const annotationsRef = useRef<AnnotationData[]>(annotations);
  annotationsRef.current = annotations;

  // State
  const [activeAnnotationId, setActiveAnnotationIdState] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioBuffers, setAudioBuffers] = useState<AudioBuffer[]>([]);
  const [peaksDataArray, setPeaksDataArray] = useState<TrackClipPeaks[]>([]); // Updated for clip-based peaks
  const [trackStates, setTrackStates] = useState<TrackState[]>([]);
  const [isAutomaticScroll, setIsAutomaticScroll] = useState(automaticScroll);
  const [continuousPlay, setContinuousPlayState] = useState(
    annotationList?.isContinuousPlay ?? false
  );
  const [linkEndpoints, setLinkEndpoints] = useState(annotationList?.linkEndpoints ?? false);
  const [annotationsEditable, setAnnotationsEditable] = useState(annotationList?.editable ?? false);
  const [isReady, setIsReady] = useState(false);
  // Bumped after every track-reorder DRAG interaction (committed or not) —
  // see bumpTrackReorderEpoch below for the full rationale.
  const [trackReorderEpoch, setTrackReorderEpoch] = useState(0);
  // Live track-reorder drag preview (view-level only; see the
  // "Track Reordering" section of packages/browser/CLAUDE.md). Set by
  // ClipInteractionProvider's onDragOver, cleared on drag end/cancel.
  // The engine's committed order is untouched until drop.
  const [trackDragPreview, setTrackDragPreview] = useState<TrackDragPreview | null>(null);

  // Refs
  // Engine owns selection, loop, selectedTrackId, zoom, and masterVolume state.
  // React subscribes to engine statechange and mirrors into useState/refs.
  // Playback timing (currentTime, isPlaying) remains in React for animation loop.
  const engineRef = useRef<PlaylistEngine | null>(null);
  const adapterRef = useRef<PlayoutAdapter | null>(null);
  const audioInitializedRef = useRef<boolean>(false);

  // Ref for animation loop access (avoids adding the prop to useCallback deps)
  const indefinitePlaybackRef = useRef(indefinitePlayback);
  indefinitePlaybackRef.current = indefinitePlayback;

  // Recording session state. While active:
  // 1. The animation loop's end-of-audio auto-stop is suppressed so overdub
  //    playback runs past the end of existing material (#589/#590).
  // 2. The armed track's existing content is transiently muted: punch-in
  //    replaces whatever the take overlaps (#579), so the doomed material
  //    must not play under the overdub. Engine-level mute (survives adapter
  //    rebuilds), captured/restored around the session; the track's UI mute
  //    state is untouched.
  // Auto-wired from PlaylistVisualization's recordingState prop; overdub
  // flows also call it eagerly before play() — a render round-trip loses the
  // race against the first animation frame (empty timeline: time 0 >=
  // duration 0 would stop playback instantly).
  const recordingActiveRef = useRef(false);
  const armedTrackMuteRef = useRef<{ trackId: string; prevMuted: boolean } | null>(null);
  const setRecordingActive = useCallback((active: boolean, armedTrackId?: string | null) => {
    recordingActiveRef.current = active;
    const engine = engineRef.current;
    if (!engine) return;
    if (active && armedTrackId) {
      if (armedTrackMuteRef.current?.trackId === armedTrackId) return; // already armed
      if (armedTrackMuteRef.current) {
        // Armed track changed mid-session — restore the previous one first.
        engine.setTrackMute(armedTrackMuteRef.current.trackId, armedTrackMuteRef.current.prevMuted);
      }
      const prevMuted = engine.getState().tracks.find((t) => t.id === armedTrackId)?.muted ?? false;
      armedTrackMuteRef.current = { trackId: armedTrackId, prevMuted };
      engine.setTrackMute(armedTrackId, true);
    } else if (!active && armedTrackMuteRef.current) {
      engine.setTrackMute(armedTrackMuteRef.current.trackId, armedTrackMuteRef.current.prevMuted);
      armedTrackMuteRef.current = null;
    }
  }, []);
  const isPlayingRef = useRef<boolean>(false);
  isPlayingRef.current = isPlaying;
  const playStartPositionRef = useRef<number>(0);
  const currentTimeRef = useRef<number>(0);
  // Visually-aligned playback time (see toVisualTime / getAudibleTime). The
  // animation loop updates this per frame. Pause/seek/stop paths sync it
  // explicitly so the static playhead reflects the commanded resting position.
  const visualTimeRef = useRef<number>(0);
  const tracksRef = useRef<ClipTrack[]>(tracks);
  const soundFontCacheRef = useRef(soundFontCache);
  soundFontCacheRef.current = soundFontCache;
  const trackStatesRef = useRef<TrackState[]>(trackStates);
  const playbackStartTimeRef = useRef<number>(0); // context.currentTime when playback started
  const audioStartPositionRef = useRef<number>(0); // Audio position when playback started
  const playbackEndTimeRef = useRef<number | null>(null); // Audio position where playback should stop (for selections)
  // Suppresses the engine 'stop' mirror (below) around provider-initiated
  // engine.stop() calls that immediately seek/restart playback in the same
  // synchronous block (play(), setSelection, reschedulePlayback, the
  // engine-rebuild-with-resume path). Without this, the mirror's ref writes
  // would race the restart's own writes and briefly show the pre-restart
  // position/isPlaying=false. Stop paths that don't restart afterward don't
  // need suppression — see the mirror's own comment for why.
  const suppressEngineStopMirrorRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isAutomaticScrollRef = useRef<boolean>(false);
  // Animation frame callback registry — components register per-frame DOM update
  // callbacks instead of running their own rAF loops. Single loop drives all.
  const frameCallbacksRef = useRef<Map<string, (data: FrameData) => void>>(new Map());
  const continuousPlayRef = useRef<boolean>(annotationList?.isContinuousPlay ?? false);
  const activeAnnotationIdRef = useRef<string | null>(null);
  // Engine clip operations guard: tracks reference from the last engine statechange.
  // When engine methods (moveClip, trimClip, splitClip) mutate tracks, the statechange
  // handler stores state.tracks here and calls onTracksChange with the same reference.
  // loadAudio checks tracks === engineTracksRef.current to skip the full rebuild.
  const engineTracksRef = useRef<ClipTrack[] | null>(null);
  // Monotonic counter from engine.getState().tracksVersion — used to detect
  // track mutations (move/trim/split/add/remove) vs other statechange events.
  const lastTracksVersionRef = useRef(0);
  // Flag set during render to prevent the previous effect cleanup from disposing the
  // engine when we're skipping the rebuild. React runs previous cleanup before the
  // current effect body, so the flag must be set during render (which precedes cleanup).
  const skipEngineDisposeRef = useRef(false);
  // Set by useClipDragHandlers during boundary trim drags. When true,
  // loadAudio skips the full engine rebuild — visual updates flow via React
  // state only. On drag end, engine.trimClip() commits the final delta.
  const isDraggingRef = useRef(false);
  // Snapshot of tracks from the previous loadAudio run, used to detect
  // additive-only changes (new tracks appended, existing unchanged).
  const prevTracksRef = useRef<ClipTrack[]>([]);
  // Provider-level ref for scroll-position math and animation loop pixel
  // calculation. Distinct from useZoomControls's internal ref (statechange guard).
  const samplesPerPixelRef = useRef<number>(initialSamplesPerPixel);
  // Provisional sample rate until the adapter (which owns the AudioContext) is
  // created in loadAudio. Reconciled from adapter.audioContext.sampleRate there.
  // (#510: avoids a static @waveform-playlist/playout import at mount.)
  const [initialSampleRate] = useState<number>(() => sampleRateProp ?? 48000);
  const sampleRateRef = useRef<number>(initialSampleRate);

  // Custom hooks — engine-owned state delegated to hooks with onEngineState() pattern
  const { timeFormat, setTimeFormat, formatTime } = useTimeFormat();
  const zoom = useZoomControls({ engineRef, initialSamplesPerPixel });
  const { samplesPerPixel, onEngineState: onZoomEngineState } = zoom;
  const volume = useMasterVolume({ engineRef, initialVolume: 1.0 });
  const {
    masterVolume,
    setMasterVolume,
    masterVolumeRef,
    onEngineState: onVolumeEngineState,
  } = volume;
  const {
    selectionStart,
    selectionEnd,
    setSelection: setSelectionEngine,
    selectionStartRef,
    selectionEndRef,
    onEngineState: onSelectionEngineState,
  } = useSelectionState({ engineRef });
  const {
    isLoopEnabled,
    loopStart,
    loopEnd,
    setLoopEnabled,
    setLoopRegion,
    clearLoopRegion,
    isLoopEnabledRef,
    loopStartRef,
    loopEndRef,
    onEngineState: onLoopEngineState,
  } = useLoopState({ engineRef });
  const {
    selectedTrackId,
    setSelectedTrackId: setSelectedTrackIdControl,
    onEngineState: onSelectedTrackEngineState,
    selectedTrackIdRef,
  } = useSelectedTrack({ engineRef });
  const {
    canUndo,
    canRedo,
    undo,
    redo,
    onEngineState: onUndoEngineState,
  } = useUndoState({ engineRef });
  const { animationFrameRef, startAnimationFrameLoop, stopAnimationFrameLoop } =
    useAnimationFrameLoop();

  // Worker-based WaveformData cache for fast zoom resampling
  const baseScale = useMemo(
    () => Math.min(...(stableZoomLevels ?? [256, 512, 1024, 2048, 4096, 8192])),
    [stableZoomLevels]
  );
  const { cache: waveformDataCache } = useWaveformDataCache(tracks, baseScale);

  // Custom setter for continuousPlay that updates BOTH state and ref synchronously
  // This ensures the ref is updated immediately, before the animation loop can read it
  const setContinuousPlay = useCallback((value: boolean) => {
    continuousPlayRef.current = value; // Update ref synchronously
    setContinuousPlayState(value); // Update state (triggers re-render)
  }, []);

  // Custom setter for activeAnnotationId that updates BOTH state and ref synchronously
  const setActiveAnnotationId = useCallback((value: string | null) => {
    activeAnnotationIdRef.current = value; // Update ref synchronously
    setActiveAnnotationIdState(value); // Update state (triggers re-render)
  }, []);

  // Cross-hook concern: reads selection refs to set loop region
  const setLoopRegionFromSelection = useCallback(() => {
    const start = selectionStartRef.current ?? 0;
    const end = selectionEndRef.current ?? 0;
    if (start !== end && end > start) {
      setLoopRegion(start, end);
    }
  }, [setLoopRegion, selectionStartRef, selectionEndRef]);

  // Keep refs in sync with state
  useEffect(() => {
    isAutomaticScrollRef.current = isAutomaticScroll;
  }, [isAutomaticScroll]);

  useEffect(() => {
    trackStatesRef.current = trackStates;
  }, [trackStates]);

  tracksRef.current = tracks;

  // Render-phase guard: detect when tracks prop came from an engine clip operation.
  // Must be set before effects run so the previous effect cleanup can skip disposal.
  // Also skip disposal during active boundary trim drags — onDragMove updates React
  // tracks per-frame, triggering effect re-runs whose cleanup would dispose the engine.
  const isEngineTracks = tracks === engineTracksRef.current;

  // Detect additive-only track changes: new tracks appended, existing tracks unchanged.
  // Uses reference equality on individual tracks — if any existing track object changed
  // (e.g., clip added to it), this is false and triggers a full rebuild.
  const prevTracks = prevTracksRef.current;
  const isIncrementalAdd =
    engineRef.current !== null &&
    tracks.length > prevTracks.length &&
    prevTracks.every((pt) => {
      const current = tracks.find((t) => t.id === pt.id);
      return current === pt; // reference equality — existing tracks must be untouched
    });
  // Note: prevTracks may be empty here (e.g. recording's first track on a
  // persistent empty-timeline engine) — that's a valid 0→1 incremental add, so
  // it must NOT require prevTracks.length > 0. The engineRef !== null guard keeps
  // it correct: a lazy/no-engine consumer still takes the full build path.

  skipEngineDisposeRef.current = isEngineTracks || isDraggingRef.current || isIncrementalAdd;

  // Adjust scroll position proportionally when zoom changes
  useEffect(() => {
    if (!scrollContainerRef.current || duration === 0) return;

    const container = scrollContainerRef.current;
    const oldSamplesPerPixel = samplesPerPixelRef.current;
    const newSamplesPerPixel = samplesPerPixel;

    if (oldSamplesPerPixel === newSamplesPerPixel) return;

    // Calculate the current center time in the viewport
    const containerWidth = container.clientWidth;
    const currentScrollLeft = container.scrollLeft;
    const centerPixel = currentScrollLeft + containerWidth / 2;
    const sr = sampleRateRef.current;
    const centerTime = (centerPixel * oldSamplesPerPixel) / sr;

    // Calculate new scroll position to keep the same center time
    const newCenterPixel = (centerTime * sr) / newSamplesPerPixel;
    const newScrollLeft = Math.max(0, newCenterPixel - containerWidth / 2);

    container.scrollLeft = newScrollLeft;
    samplesPerPixelRef.current = newSamplesPerPixel;
  }, [samplesPerPixel, duration]);

  // Track pending playback resume after tracks change
  const pendingResumeRef = useRef<{ position: number } | null>(null);

  // Load audio from clips (only when tracks change)
  useEffect(() => {
    // Guard: skip full rebuild when tracks came from an engine clip operation
    // (moveClip, trimClip, splitClip). The engine and adapter already have the
    // correct tracks — we just need to update duration for the timeline.
    // Also skip during active boundary trim drags — visual updates flow via
    // React state only; engine.trimClip() commits the final delta on drag end.
    if (isEngineTracks || isDraggingRef.current) {
      if (isEngineTracks) {
        engineTracksRef.current = null;
      }

      // Recalculate duration from updated clip positions
      let maxDuration = 0;
      tracks.forEach((track) => {
        track.clips.forEach((clip) => {
          const clipEnd = (clip.startSample + clip.durationSamples) / clip.sampleRate;
          maxDuration = Math.max(maxDuration, clipEnd);
        });
      });
      setDuration(maxDuration);
      prevTracksRef.current = tracks;
      return;
    }

    // Guard: incremental track addition — only new tracks appended, existing
    // tracks unchanged. Add each new track to the existing engine without
    // tearing down the playout. This avoids disposing and recreating all
    // audio nodes when dropping a file or finishing a recording.
    if (isIncrementalAdd && engineRef.current) {
      try {
        // Use prevTracks captured during render (not prevTracksRef.current) to ensure
        // the guard condition and inner logic operate on the same snapshot.
        const prevIds = new Set(prevTracks.map((t) => t.id));
        const addedTracks = tracks.filter((t) => !prevIds.has(t.id));

        // Merge current UI state into new tracks before adding to engine
        const currentTrackStates = trackStatesRef.current;
        for (const track of addedTracks) {
          const trackIndex = tracks.indexOf(track);
          const trackState = currentTrackStates[trackIndex];
          const trackWithState = {
            ...track,
            volume: trackState?.volume ?? track.volume,
            muted: trackState?.muted ?? track.muted,
            soloed: trackState?.soloed ?? track.soloed,
            pan: trackState?.pan ?? track.pan,
          };
          engineRef.current!.addTrack(trackWithState);
        }

        // Update duration from all tracks (including newly added)
        let maxDuration = 0;
        tracks.forEach((track) => {
          track.clips.forEach((clip) => {
            const clipEnd = (clip.startSample + clip.durationSamples) / clip.sampleRate;
            maxDuration = Math.max(maxDuration, clipEnd);
          });
        });
        setDuration(maxDuration);

        // Initialize track states for the new tracks (preserve existing)
        setTrackStates((prev) => {
          if (prev.length === tracks.length) return prev;
          const newStates = [...prev];
          for (const track of addedTracks) {
            newStates.push({
              name: track.name,
              muted: track.muted,
              soloed: track.soloed,
              volume: track.volume,
              pan: track.pan,
            });
          }
          return newStates;
        });

        // Extract audio buffers from all clips across all tracks
        const buffers: AudioBuffer[] = [];
        tracks.forEach((track) => {
          track.clips.forEach((clip) => {
            if (clip.audioBuffer) {
              buffers.push(clip.audioBuffer);
            }
          });
        });
        setAudioBuffers(buffers);

        prevTracksRef.current = tracks;
        return;
      } catch (err) {
        console.warn(
          '[waveform-playlist] Incremental add failed, falling through to full rebuild:',
          String(err)
        );
        // Fall through to full loadAudio path
      }
    }

    // Defer engine rebuild during progressive loading — tracks render visually
    // (controls, clip shapes, peaks via worker) but the engine isn't built yet.
    // When deferEngineRebuild flips to false, this effect re-runs and builds once.
    if (deferEngineRebuild) {
      // Still update duration so the timeline renders at the correct width
      let maxDuration = 0;
      tracks.forEach((track) => {
        track.clips.forEach((clip) => {
          const clipEnd = (clip.startSample + clip.durationSamples) / clip.sampleRate;
          maxDuration = Math.max(maxDuration, clipEnd);
        });
      });
      setDuration(maxDuration);
      return;
    }

    // Reset ready state for full rebuild
    setIsReady(false);

    if (tracks.length === 0) {
      // Clear content state when all tracks are removed.
      setAudioBuffers([]);
      setDuration(0);
      setTrackStates([]);
      setPeaksDataArray([]);

      // Always keep a persistent engine alive — even with no tracks — so the
      // Transport is ready before playback/recording starts. Without this the
      // engine is rebuilt asynchronously when the first track is added, and
      // play() races that rebuild — intermittently no-opping (frozen
      // playhead/time) and paying first-record latency. The engine is still
      // created via dynamic import (lazy module load preserved, #510); only its
      // lifecycle is eager.
      if (engineRef.current) {
        // Keep the existing engine; just clear its tracks.
        engineRef.current.setTracks([]);
        prevTracksRef.current = tracks;
        return;
      }
      // No engine yet (initial mount with no tracks): fall through to the
      // loadAudio path below, which builds a persistent engine for 0 tracks.
    }

    // Capture playback state before rebuilding playout (read from ref, not state,
    // so isPlaying can be excluded from the dep array — play/pause must not trigger rebuilds)
    const wasPlaying = isPlayingRef.current;
    const resumePosition = currentTimeRef.current;

    // Stop current playback and animation before disposing
    if (engineRef.current && wasPlaying) {
      // Suppressed: resumePlayback() (below) restarts from resumePosition once
      // the rebuilt engine is ready — an unguarded mirror would clobber the
      // refs with this (old, about-to-be-replaced) engine's rewound position
      // in the meantime. See suppressEngineStopMirrorRef declaration.
      suppressEngineStopMirrorRef.current = true;
      engineRef.current.stop();
      suppressEngineStopMirrorRef.current = false;
      stopAnimationFrameLoop();
      // Mark that we need to resume playback after playout is rebuilt
      pendingResumeRef.current = { position: resumePosition };
    }

    let cancelled = false;

    const loadAudio = async () => {
      try {
        // Extract all audio buffers from all clips across all tracks
        const buffers: AudioBuffer[] = [];

        tracks.forEach((track) => {
          track.clips.forEach((clip) => {
            if (clip.audioBuffer) {
              buffers.push(clip.audioBuffer);
            }
          });
        });

        // Calculate total timeline duration from all clips across all tracks
        // Use clip.sampleRate which is always defined (works for peaks-only clips too)
        let maxDuration = 0;
        tracks.forEach((track) => {
          track.clips.forEach((clip) => {
            const sampleRate = clip.sampleRate;
            const clipEndSample = clip.startSample + clip.durationSamples;
            const clipEnd = clipEndSample / sampleRate;
            maxDuration = Math.max(maxDuration, clipEnd);
          });
        });

        setAudioBuffers(buffers);
        setDuration(maxDuration);

        // Initialize or update track states, preserving existing UI state (mute/solo/volume/pan)
        // Only initialize from ClipTrack props when trackStates is empty or track count changes
        setTrackStates((prevStates) => {
          if (prevStates.length === tracks.length) {
            // Same number of tracks - preserve existing UI state, just update names
            return prevStates.map((state, i) => ({
              ...state,
              name: tracks[i].name,
            }));
          }
          // Track count changed - reinitialize from ClipTrack properties
          return tracks.map((track) => ({
            name: track.name,
            muted: track.muted,
            soloed: track.soloed,
            volume: track.volume,
            pan: track.pan,
          }));
        });

        // Dispose old engine before creating new one
        if (engineRef.current) {
          engineRef.current.dispose();
        }

        // Reset tracks version tracking for the new engine
        lastTracksVersionRef.current = 0;
        engineTracksRef.current = null;

        // Create engine adapter — custom if provided, else Tone.js (dynamically imported).
        // Reset init flag — new adapter needs AudioContext resume on first play.
        audioInitializedRef.current = false;
        const adapter = await resolvePlayoutAdapter({
          createAdapter,
          effects,
          soundFontCache: soundFontCacheRef.current,
          sampleRate: sampleRateProp,
        });
        if (cancelled) {
          adapter.dispose();
          return;
        }
        // Reconcile sample rate from the adapter's own AudioContext. The setState
        // calls later in loadAudio trigger a re-render that propagates this via
        // `const sampleRate = sampleRateRef.current` at render.
        sampleRateRef.current = adapter.audioContext.sampleRate;
        adapterRef.current = adapter;
        const engine = new PlaylistEngine({
          adapter,
          samplesPerPixel: samplesPerPixelRef.current,
          zoomLevels: stableZoomLevels,
        });

        // Seed engine with current UI state so a fresh engine doesn't
        // reset selection/loop/selectedTrack to defaults on the first statechange emission.
        engine.setSelection(selectionStartRef.current ?? 0, selectionEndRef.current ?? 0);
        engine.setLoopRegion(loopStartRef.current ?? 0, loopEndRef.current ?? 0);
        if (isLoopEnabledRef.current) engine.setLoopEnabled(true);
        engine.setMasterVolume(masterVolumeRef.current ?? 1.0);
        if (selectedTrackIdRef.current) engine.selectTrack(selectedTrackIdRef.current);

        // Merge current UI state into tracks before passing to engine
        const currentTrackStates = trackStatesRef.current;
        const tracksWithState = tracks.map((track, index) => {
          const trackState = currentTrackStates[index];
          return {
            ...track,
            volume: trackState?.volume ?? track.volume,
            muted: trackState?.muted ?? track.muted,
            soloed: trackState?.soloed ?? track.soloed,
            pan: trackState?.pan ?? track.pan,
          };
        });

        // Subscribe to engine statechange BEFORE setTracks() so the first
        // emission (which carries correct canZoomIn/canZoomOut) is captured.
        // Seeding statechanges fired above are harmless — they contain values
        // that already match the hooks' current state, so onEngineState()
        // ref guards skip them.
        // Suppress tracks mirroring during the initial setTracks — the parent
        // already has these tracks. The flag is local so it becomes false
        // after setTracks returns (statechange fires synchronously).
        let suppressTracksMirroring = true;
        engine.on('statechange', (state: EngineState) => {
          onSelectionEngineState(state);
          onLoopEngineState(state);
          onSelectedTrackEngineState(state);
          onZoomEngineState(state);
          onVolumeEngineState(state);
          onUndoEngineState(state);

          // Mirror engine tracks changes to parent via onTracksChange.
          // tracksVersion only increments on structural track mutations (move,
          // trim, split, setTracks, addTrack, removeTrack), not on
          // selection/zoom/volume changes. Per-track mixer edits bump the
          // engine's separate mixerVersion (#501) and are persisted here via
          // trackStates, so they intentionally do not flow through onTracksChange.
          if (!suppressTracksMirroring && state.tracksVersion !== lastTracksVersionRef.current) {
            lastTracksVersionRef.current = state.tracksVersion;
            engineTracksRef.current = state.tracks;

            // trackStates is positionally indexed (no track id of its own) and
            // is otherwise never touched by reorderTrack — the engine reorder is
            // "purely organizational" (see PlaylistEngine.reorderTrack) and never
            // rebuilds. Without this, a track's mixer identity (name/mute/solo/
            // volume/pan) stays pinned to its OLD index after a reorder, so the
            // control panel silently shows/controls the wrong track. Re-derive an
            // id->state map from the pre-reorder pairing (tracksRef/trackStatesRef
            // reflect the last committed render, i.e. before this mutation) and
            // re-project it onto the new track order. Guarded to same-length
            // arrays (addTrack/removeTrack already flow through a full rebuild
            // that reinitializes trackStates) and to an actual order change (so
            // non-reorder structural mutations like moveClip/trimClip/splitClip
            // — which don't change track order — are a no-op here).
            const oldTracks = tracksRef.current;
            const oldStates = trackStatesRef.current;
            if (oldTracks.length === oldStates.length && oldTracks.length === state.tracks.length) {
              const idToState = new Map(oldTracks.map((t, i) => [t.id, oldStates[i]]));
              const reordered = state.tracks.map((t, i) => idToState.get(t.id) ?? oldStates[i]);
              const orderChanged = reordered.some((s, i) => s !== oldStates[i]);
              if (orderChanged) {
                setTrackStates(reordered);
              }
            }

            if (onTracksChange) {
              onTracksChange(state.tracks);
            } else {
              console.warn(
                '[waveform-playlist] Engine tracks changed but onTracksChange prop is not set — ' +
                  'UI will revert on next render. Pass onTracksChange to WaveformPlaylistProvider.'
              );
            }
          }
        });

        // Engine-initiated stop mirror (final-review CRITICAL 1, #608 follow-up).
        // PlaylistEngine self-stops when a bounded play(start, end) completes on
        // an adapter that reports onPlaybackEnded (Tone) — see engine CLAUDE.md
        // "onPlaybackEnded hook". That self-stop rewinds the engine's own current
        // time to playStartPosition BEFORE the rAF loop's own end-of-playback
        // checks (below) can observe it, so the loop's `time >= playbackEndTimeRef`
        // / `time >= duration` comparisons never fire — React isPlaying stays
        // true and the loop spins forever at the rewound position. Mirroring the
        // engine's 'stop' event makes React authoritative-from-engine for ANY
        // engine-initiated stop, closing that race. The engine already rewound
        // its own time (`_currentTime = _playStartPosition`) — we only mirror
        // that into React's refs/state, we never seek() again here.
        //
        // Suppressed via suppressEngineStopMirrorRef around provider-initiated
        // engine.stop() calls that immediately seek/restart playback (play(),
        // setSelection, reschedulePlayback, engine-rebuild-with-resume) — those
        // already set the correct target position themselves; an unguarded
        // mirror would clobber it mid-transition with the pre-restart position.
        // Stop paths that terminate playback (stop(), the rAF loop's own
        // annotation/selection/duration-end branches) don't need suppression:
        // they either write the identical value the mirror would (both read
        // _playStartPosition), or run their own setCurrentTimeRefs call
        // synchronously right after this listener, which — as plain refs, not
        // batched state — simply overwrites the mirror's write with no other
        // code observing the transient value in between.
        engine.on('stop', () => {
          if (suppressEngineStopMirrorRef.current) return;
          setIsPlaying(false);
          stopAnimationFrameLoop();
          const stoppedAt = engine.getCurrentTime();
          setCurrentTimeRefs(stoppedAt);
          setCurrentTime(stoppedAt);
        });

        engine.setTracks(tracksWithState);
        suppressTracksMirroring = false;
        lastTracksVersionRef.current = engine.getState().tracksVersion;
        engineRef.current = engine;

        setIsReady(true);

        // Dispatch custom event for external listeners
        const event = new CustomEvent('waveform-playlist:ready', {
          detail: {
            trackCount: tracks.length,
            duration: maxDuration,
          },
        });
        window.dispatchEvent(event);

        prevTracksRef.current = tracks;

        onReady?.();
      } catch (error) {
        // Dispose an adapter created before the engine failed to build (e.g. invalid
        // zoomLevels make `new PlaylistEngine` throw) — the cleanup skips it because
        // engineRef was never assigned, so it would otherwise leak a live AudioContext.
        if (!engineRef.current && adapterRef.current) {
          try {
            adapterRef.current.dispose();
          } catch (disposeErr) {
            console.warn(
              '[waveform-playlist] adapter dispose after load failure threw: ' + String(disposeErr)
            );
          }
          adapterRef.current = null;
        }
        console.warn('[waveform-playlist] Error loading audio: ' + String(error));
        onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    };

    loadAudio();

    return () => {
      cancelled = true;
      // Skip disposal when the next render's guard will keep the engine alive.
      // skipEngineDisposeRef is set during the render phase (before this cleanup runs).
      if (skipEngineDisposeRef.current) {
        skipEngineDisposeRef.current = false;
        return;
      }
      stopAnimationFrameLoop();
      if (engineRef.current) {
        engineRef.current.dispose();
        adapterRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tracks,
    // isEngineTracks is intentionally excluded — it's a render-phase guard read
    // inside the effect body, not a trigger. Including it causes a spurious re-run
    // when it flips from true→false after engineTracksRef is cleared.
    // isPlaying is intentionally excluded — read from isPlayingRef inside the
    // effect body. Including it causes a full engine+playout rebuild on every
    // play/pause/stop, destroying and recreating all audio Players.
    // soundFontCache is deliberately excluded — late cache changes are forwarded
    // to the live adapter by the sync effect below; only adapter creation reads it
    // (via soundFontCacheRef).
    onReady,
    onError,
    effects,
    stopAnimationFrameLoop,
    onSelectionEngineState,
    onLoopEngineState,
    onSelectedTrackEngineState,
    onZoomEngineState,
    onVolumeEngineState,
    onUndoEngineState,
    onTracksChange,
    masterVolumeRef,
    selectionStartRef,
    selectionEndRef,
    loopStartRef,
    loopEndRef,
    isLoopEnabledRef,
    stableZoomLevels,
    deferEngineRebuild,
  ]);

  // Forward late-arriving / swapped SoundFontCache to the live adapter so
  // MIDI tracks upgrade from PolySynth without an engine rebuild. On mount
  // this may run against a just-created adapter (the loadAudio effect is
  // declared earlier, runs first, and assigns adapterRef synchronously) or
  // against null (empty tracks). Both are safe: the helper no-ops on null,
  // and the adapter skips MIDI tracks whose routing is unchanged.
  useEffect(() => {
    syncSoundFontCacheToAdapter(adapterRef.current, soundFontCache);
  }, [soundFontCache]);

  // Regenerate peaks when zoom, mono, or waveformDataCache changes (without reloading audio)
  // Peak sources in priority order:
  //   A) clip.waveformData — external pre-computed peaks (e.g. from BBC audiowaveform)
  //   B) waveformDataCache — worker-generated WaveformData (fast resample on zoom)
  //   C) empty peaks — clip is still loading or has no audio data
  useEffect(() => {
    if (tracks.length === 0) return;

    const allTrackPeaks: TrackClipPeaks[] = tracks.map((track) => {
      const clipPeaks: ClipPeaks[] = track.clips.map((clip) => {
        let peaks: PeakData | undefined;

        // Path A: External pre-computed waveform data (e.g. from audiowaveform .dat file)
        // When sample rates match, use clip offsets directly.
        // When rates mismatch (e.g., waveformData at 48000 Hz, clip rebuilt at 44100 Hz
        // after audio decode), convert offsets to the waveformData's sample space and
        // adjust samplesPerPixel so peaks fill the correct container width.
        // Path B (worker) replaces these on next render once cache is populated.
        if (clip.waveformData) {
          try {
            const wdRate = clip.waveformData.sample_rate;
            const clipRate = clip.sampleRate;
            let peakOffset = clip.offsetSamples;
            let peakDuration = clip.durationSamples;
            let peakSpp = samplesPerPixel;
            if (wdRate !== clipRate && clipRate > 0 && wdRate > 0) {
              const ratio = wdRate / clipRate;
              peakOffset = Math.round(clip.offsetSamples * ratio);
              peakDuration = Math.round(clip.durationSamples * ratio);
              peakSpp = Math.max(1, Math.round(samplesPerPixel * ratio));
            }
            peaks = extractPeaksFromWaveformDataFull(
              clip.waveformData as WaveformData,
              peakSpp,
              mono,
              peakOffset,
              peakDuration
            );
          } catch (err) {
            console.warn(
              '[waveform-playlist] Failed to extract peaks from waveformData: ' + String(err)
            );
          }
        }

        // Path B: Worker-generated WaveformData cache (fast resample on zoom)
        if (!peaks) {
          const cached = waveformDataCache.get(clip.id);
          if (cached) {
            try {
              peaks = extractPeaksFromWaveformDataFull(
                cached,
                samplesPerPixel,
                mono,
                clip.offsetSamples,
                clip.durationSamples
              );
            } catch (err) {
              console.warn(
                '[waveform-playlist] Failed to extract peaks from cache: ' + String(err)
              );
            }
          }
        }

        // Path C: No peaks data available yet — render empty while worker processes.
        // Use correct channel count from audioBuffer to prevent track height shift
        // when peaks arrive (mono mode collapses to 1 channel).
        if (!peaks) {
          if (!deferEngineRebuild && !clip.audioBuffer && !clip.waveformData && !clip.midiNotes) {
            console.warn(
              `[waveform-playlist] Clip "${clip.id}" has no audio data or waveform data`
            );
          }
          const numChannels = mono ? 1 : (clip.audioBuffer?.numberOfChannels ?? 1);
          // MIDI clips: derive pixel width from sample duration so the clip renders
          // at the correct width instead of zero-width blank tracks.
          const pixelLength = clip.midiNotes
            ? Math.ceil(clip.durationSamples / samplesPerPixel)
            : 0;
          peaks = {
            length: pixelLength,
            data: Array.from({ length: numChannels }, () => new Int16Array(pixelLength * 2)),
            bits: 16,
          };
        }

        return {
          clipId: clip.id,
          trackName: track.name,
          peaks,
          startSample: clip.startSample,
          durationSamples: clip.durationSamples,
          fadeIn: clip.fadeIn,
          fadeOut: clip.fadeOut,
          midiNotes: clip.midiNotes,
          sampleRate: clip.sampleRate,
          offsetSamples: clip.offsetSamples,
        };
      });

      return clipPeaks;
    });

    setPeaksDataArray(allTrackPeaks);
  }, [tracks, samplesPerPixel, mono, waveformDataCache, deferEngineRebuild]);

  // Audio clock from the adapter's own AudioContext (#510 — replaces tone's
  // getContext()). Stable; reads a ref so identity never changes.
  const getAudioContextTime = useCallback(
    () => adapterRef.current?.audioContext.currentTime ?? 0,
    []
  );

  // Returns current playback time from engine (auto-wraps at loop boundaries).
  // Falls back to manual calculation when engine is unavailable.
  const getPlaybackTimeFallbackWarnedRef = useRef(false);
  const getPlaybackTime = useCallback(() => {
    if (engineRef.current) {
      return engineRef.current.getCurrentTime();
    }
    // Fallback: manual calculation (does not handle loop wrapping)
    if (!getPlaybackTimeFallbackWarnedRef.current) {
      getPlaybackTimeFallbackWarnedRef.current = true;
      console.warn(
        '[waveform-playlist] getPlaybackTime called without engine. ' +
          'Falling back to manual elapsed time (loop wrapping will not work).'
      );
    }
    const elapsed = getAudioContextTime() - (playbackStartTimeRef.current ?? 0);
    return (audioStartPositionRef.current ?? 0) + elapsed;
  }, [getAudioContextTime]);

  // Resting (non-playing) cursor positions display the raw time unchanged.
  // Latency compensation (outputLatency + lookAhead) models the gap between
  // the Transport's scheduling position and audible output — a property of
  // *playing*, not of position. A seeked/paused/stopped cursor has no audible
  // counterpart; the commanded position is the truth. During playback the
  // animation loop uses engine.getAudibleTime() instead.
  const toVisualTime = useCallback((rawTime: number): number => {
    return Number.isFinite(rawTime) ? Math.max(0, rawTime) : 0;
  }, []);

  // Pair-write helper for the dual-ref pattern. Storage stays raw on
  // `currentTimeRef`; display gets the guard-clamped raw version on `visualTimeRef`
  // (compensation happens in the animation loop via engine.getAudibleTime() while playing).
  // Use this at every pause/seek/stop/loop site — never assign one ref alone
  // (browser/CLAUDE.md: "Display uses visualTimeRef").
  const setCurrentTimeRefs = useCallback(
    (rawTime: number) => {
      currentTimeRef.current = rawTime;
      visualTimeRef.current = toVisualTime(rawTime);
    },
    [toVisualTime]
  );

  // Read the engine's scheduler lookahead (Tone adapter ~0.1s, native 0).
  // Exposed through PlaybackAnimationContextValue so consumers (e.g., recording
  // live preview) compute audible-latency from the same source as the playhead.
  const getLookAhead = useCallback((): number => {
    return engineRef.current?.lookAhead ?? 0;
  }, []);

  const getOutputLatency = useCallback((): number => {
    const audioCtx = adapterRef.current?.audioContext;
    return audioCtx && 'outputLatency' in audioCtx ? (audioCtx as AudioContext).outputLatency : 0;
  }, []);

  const registerFrameCallback = useCallback((id: string, cb: (data: FrameData) => void) => {
    frameCallbacksRef.current.set(id, cb);
  }, []);

  const unregisterFrameCallback = useCallback((id: string) => {
    frameCallbacksRef.current.delete(id);
  }, []);

  // Animation loop
  const startAnimationLoop = useCallback(() => {
    const updateTime = () => {
      // Get current time from engine (auto-wraps at loop boundaries via Transport.seconds)
      const time = getPlaybackTime();
      currentTimeRef.current = time;

      // Compute visual time once — all visual consumers use this same value.
      // engine.getAudibleTime() subtracts outputLatency (hardware DAC delay)
      // and adapter.lookAhead (Tone.js Transport runs ~100ms ahead of audible),
      // holding at the play-start position during the pre-roll window. Native
      // adapters report lookAhead 0, so this is a near no-op there.
      const visualRaw = engineRef.current ? engineRef.current.getAudibleTime() : time;
      const visualTime = Number.isFinite(visualRaw) ? Math.max(0, visualRaw) : 0;
      visualTimeRef.current = visualTime;

      // Drive registered per-frame callbacks BEFORE stop checks so the
      // final frame renders at the correct stop position (not one frame behind).
      const sr = sampleRateRef.current;
      const spp = samplesPerPixelRef.current;
      const frameData: FrameData = {
        time,
        visualTime,
        sampleRate: sr,
        samplesPerPixel: spp,
      };
      for (const cb of frameCallbacksRef.current.values()) {
        cb(frameData);
      }

      // Handle annotation playback based on continuous play mode
      const currentAnnotations = annotationsRef.current;
      if (currentAnnotations.length > 0) {
        const currentAnnotation = currentAnnotations.find(
          (ann) => time >= ann.start && time < ann.end
        );

        if (continuousPlayRef.current) {
          // Continuous play ON: update active annotation, let audio play to the end
          if (currentAnnotation && currentAnnotation.id !== activeAnnotationIdRef.current) {
            setActiveAnnotationId(currentAnnotation.id);
          } else if (!currentAnnotation && activeAnnotationIdRef.current !== null) {
            // Clear the active annotation when we're past it, but don't stop playback
            // Let playback continue until the audio actually ends (handled by duration check)
            setActiveAnnotationId(null);
          }
        } else {
          // Continuous play OFF: stop at end of current annotation
          if (activeAnnotationIdRef.current) {
            const activeAnnotation = currentAnnotations.find(
              (ann) => ann.id === activeAnnotationIdRef.current
            );
            if (activeAnnotation && time >= activeAnnotation.end) {
              // Stop playback at end of current annotation
              if (engineRef.current) {
                engineRef.current.stop();
              }
              setIsPlaying(false);
              setCurrentTimeRefs(playStartPositionRef.current);
              setCurrentTime(playStartPositionRef.current);
              return;
            }
          } else {
            // If no active annotation ID is set, use the current annotation
            if (currentAnnotation) {
              setActiveAnnotationId(currentAnnotation.id);
            }
          }
        }
      }

      // Handle automatic scroll — uses shared visualTime for alignment with playhead
      if (isAutomaticScrollRef.current && scrollContainerRef.current && duration > 0) {
        const container = scrollContainerRef.current;
        const pixelPosition = (visualTime * sr) / spp;
        const containerWidth = container.clientWidth;

        // Continuously scroll to keep playhead centered.
        // Math.round prevents sub-pixel jitter from browser integer rounding.
        const targetScrollLeft = Math.round(Math.max(0, pixelPosition - containerWidth / 2));
        container.scrollLeft = targetScrollLeft;
      }

      // Check if we've reached the playback end time (for selection playback)
      if (playbackEndTimeRef.current !== null && time >= playbackEndTimeRef.current) {
        // Stop playback at selection end (selection playback is separate from looping)
        if (engineRef.current) {
          engineRef.current.stop();
        }
        setIsPlaying(false);
        setCurrentTimeRefs(playbackEndTimeRef.current);
        setCurrentTime(playbackEndTimeRef.current);
        playbackEndTimeRef.current = null; // Clear the end time
        return;
      }

      // Loop is handled natively by Tone.js Transport (Transport.loop/loopStart/loopEnd).
      // Transport.seconds auto-wraps at loop boundaries, so getPlaybackTime() returns
      // the correct position without manual detection here.

      // Auto-stop at the end of the timeline (player-style default), unless:
      // - indefinitePlayback: the transport rolls until an explicit
      //   stop/pause (DAW-style, matching dawcore's indefinite-playback), or
      // - a recording session is active: overdub playback must run past the
      //   end of existing material (#589/#590).
      // Explicit ends (selection playback, annotation boundaries) are
      // handled above regardless.
      if (time >= duration && !indefinitePlaybackRef.current && !recordingActiveRef.current) {
        // Stop playback - inline to avoid circular dependency
        if (engineRef.current) {
          engineRef.current.stop();
        }
        setIsPlaying(false);
        setCurrentTimeRefs(playStartPositionRef.current);
        setCurrentTime(playStartPositionRef.current);
        setActiveAnnotationId(null);
        return;
      }
      startAnimationFrameLoop(updateTime);
    };
    startAnimationFrameLoop(updateTime);
  }, [
    duration,
    setActiveAnnotationId,
    startAnimationFrameLoop,
    getPlaybackTime,
    setCurrentTimeRefs,
  ]);

  const stopAnimationLoop = stopAnimationFrameLoop;

  // Restart animation loop and reschedule playout when continuousPlay changes during playback
  // This ensures the loop always has the current continuousPlay value
  // and removes duration limits when switching to continuous play
  useEffect(() => {
    const reschedulePlayback = async () => {
      if (isPlaying && animationFrameRef.current && engineRef.current) {
        // When toggling continuous play ON, reschedule playout without duration limit
        // so audio continues past the current annotation boundary
        if (continuousPlay) {
          const currentPos = currentTimeRef.current;

          // Stop current playout (which may have duration limit + pause callback).
          // Suppressed: this immediately restarts playback below — see
          // suppressEngineStopMirrorRef declaration.
          suppressEngineStopMirrorRef.current = true;
          engineRef.current.stop();
          suppressEngineStopMirrorRef.current = false;
          stopAnimationLoop();

          const timeNow = getAudioContextTime();
          playbackStartTimeRef.current = timeNow;
          audioStartPositionRef.current = currentPos;

          // Play without duration - will play to end of track
          engineRef.current.play(currentPos);
          startAnimationLoop();
        } else {
          // Just restart animation loop for continuous play OFF
          stopAnimationLoop();
          startAnimationLoop();
        }
      }
    };

    reschedulePlayback().catch((err) => {
      console.warn('[waveform-playlist] Failed to reschedule playback:', err);
      setIsPlaying(false);
      stopAnimationLoop();
    });
  }, [
    continuousPlay,
    isPlaying,
    startAnimationLoop,
    stopAnimationLoop,
    animationFrameRef,
    getAudioContextTime,
  ]);

  // Resume playback after tracks change (e.g., after splitting a clip during playback)
  useEffect(() => {
    const resumePlayback = async () => {
      if (pendingResumeRef.current && engineRef.current) {
        const { position } = pendingResumeRef.current;
        pendingResumeRef.current = null;

        const timeNow = getAudioContextTime();
        playbackStartTimeRef.current = timeNow;
        audioStartPositionRef.current = position;

        if (!audioInitializedRef.current) {
          await engineRef.current.init();
          audioInitializedRef.current = true;
        }

        engineRef.current.play(position);
        setIsPlaying(true);
        startAnimationLoop();
      }
    };

    resumePlayback().catch((err) => {
      console.warn('[waveform-playlist] Failed to resume playback after track change:', err);
      setIsPlaying(false);
      stopAnimationLoop();
    });
  }, [tracks, startAnimationLoop, stopAnimationLoop, getAudioContextTime]);

  // Playback controls
  const play = useCallback(
    async (startTime?: number, playDuration?: number) => {
      if (!engineRef.current) return;

      const actualStartTime = startTime ?? currentTimeRef.current;
      playStartPositionRef.current = actualStartTime;

      // Update currentTimeRef to match the actual start position
      // This ensures the animation loop starts from the correct position
      setCurrentTimeRefs(actualStartTime);

      // Stop existing playback and animation loop before restarting.
      // engine.stop() resets engine._currentTime to _playStartPosition (could be 0)
      // and sets _isPlaying=false. AnimatedPlayhead's RAF may fire during this
      // window and call engine.getCurrentTime() which would return 0.
      // The seek() immediately sets _currentTime to the intended start position,
      // preventing the cursor flash.
      // Suppressed: seek()+play() below immediately re-establish the correct
      // position — see suppressEngineStopMirrorRef declaration.
      suppressEngineStopMirrorRef.current = true;
      engineRef.current.stop();
      suppressEngineStopMirrorRef.current = false;
      engineRef.current.seek(actualStartTime);
      stopAnimationLoop();

      // Record timing for accurate position tracking using the adapter's AudioContext
      const startTimeNow = getAudioContextTime();
      playbackStartTimeRef.current = startTimeNow;
      audioStartPositionRef.current = actualStartTime;

      // Set playback end time if playing with duration (e.g., selection playback)
      playbackEndTimeRef.current =
        playDuration !== undefined ? actualStartTime + playDuration : null;

      // Don't set up playback complete callback for annotations
      // The animation loop handles stopping at annotation boundaries
      // This avoids callback timing issues when switching between annotations

      // Ensure AudioContext is resumed (requires user gesture, no-op after first call).
      // Guarded by ref so subsequent plays skip the await entirely.
      if (!audioInitializedRef.current) {
        await engineRef.current.init();
        audioInitializedRef.current = true;
      }

      const endTime = playDuration !== undefined ? actualStartTime + playDuration : undefined;
      try {
        engineRef.current.play(actualStartTime, endTime);
      } catch (err) {
        console.warn('[waveform-playlist] Playback failed to start:', err);
        stopAnimationLoop();
        return;
      }
      setIsPlaying(true);
      startAnimationLoop();
    },
    [startAnimationLoop, stopAnimationLoop, setCurrentTimeRefs, getAudioContextTime]
  );

  const pause = useCallback(() => {
    if (!engineRef.current) return;

    // Get current position from engine (auto-wraps at loop boundaries)
    const pauseTime = getPlaybackTime();

    engineRef.current.pause();
    setIsPlaying(false);
    stopAnimationLoop();

    // Update to the calculated pause position
    setCurrentTimeRefs(pauseTime);
    setCurrentTime(pauseTime);
  }, [stopAnimationLoop, getPlaybackTime, setCurrentTimeRefs]);

  const stop = useCallback(() => {
    if (!engineRef.current) return;

    engineRef.current.stop();
    setIsPlaying(false);
    stopAnimationLoop();

    // Return cursor to where playback started (Audacity-style)
    setCurrentTimeRefs(playStartPositionRef.current);
    setCurrentTime(playStartPositionRef.current);
    setActiveAnnotationId(null);
  }, [stopAnimationLoop, setActiveAnnotationId, setCurrentTimeRefs]);

  // Seek to a specific time - works whether playing or stopped
  const seekTo = useCallback(
    (time: number) => {
      // Clamp time to valid range
      const clampedTime = Math.max(0, Math.min(time, duration));

      // Update the current time state
      setCurrentTimeRefs(clampedTime);
      setCurrentTime(clampedTime);

      // If currently playing, restart at the new position.
      // play() handles stop+seek+restart internally.
      if (isPlaying && engineRef.current) {
        play(clampedTime);
      }
    },
    [duration, isPlaying, play, setCurrentTimeRefs]
  );

  // Track controls
  const setTrackMute = useCallback(
    (trackIndex: number, muted: boolean) => {
      const trackId = tracksRef.current[trackIndex]?.id;
      if (!trackId) return;

      const newStates = [...trackStates];
      newStates[trackIndex] = { ...newStates[trackIndex], muted };
      setTrackStates(newStates);

      if (engineRef.current) {
        engineRef.current.setTrackMute(trackId, muted);
      }
    },
    [trackStates]
  );

  const setTrackSolo = useCallback(
    (trackIndex: number, soloed: boolean) => {
      const trackId = tracksRef.current[trackIndex]?.id;
      if (!trackId) return;

      const newStates = [...trackStates];
      newStates[trackIndex] = { ...newStates[trackIndex], soloed };
      setTrackStates(newStates);

      if (engineRef.current) {
        engineRef.current.setTrackSolo(trackId, soloed);
      }
    },
    [trackStates]
  );

  const setTrackVolume = useCallback(
    (trackIndex: number, volume: number) => {
      const trackId = tracksRef.current[trackIndex]?.id;
      if (!trackId) return;

      const newStates = [...trackStates];
      newStates[trackIndex] = { ...newStates[trackIndex], volume };
      setTrackStates(newStates);

      if (engineRef.current) {
        engineRef.current.setTrackVolume(trackId, volume);
      }
    },
    [trackStates]
  );

  const setTrackPan = useCallback(
    (trackIndex: number, pan: number) => {
      const trackId = tracksRef.current[trackIndex]?.id;
      if (!trackId) return;

      const newStates = [...trackStates];
      newStates[trackIndex] = { ...newStates[trackIndex], pan };
      setTrackStates(newStates);

      if (engineRef.current) {
        engineRef.current.setTrackPan(trackId, pan);
      }
    },
    [trackStates]
  );

  const reorderTrack = useCallback(
    (trackId: string, toIndex: number) => {
      if (!engineRef.current) {
        console.warn('[waveform-playlist] reorderTrack: engine not ready, call ignored');
        return;
      }
      engineRef.current.reorderTrack(trackId, toIndex);
    },
    [engineRef]
  );

  // @dnd-kit/react's Feedback and OptimisticSortingPlugin sortable plugins
  // perform raw, React-bypassing DOM node moves (insertAdjacentElement /
  // replaceWith) as part of their live drag-preview and collision-commit
  // mechanics — confirmed via MutationObserver instrumentation to physically
  // relocate the React-owned track-controls row during a track-reorder drag,
  // leaving its parent/nextSibling different from what React's fiber tree
  // still believes. That desync is permanent for the session: every LATER
  // React-driven reorder (button OR drag) computes correct data but silently
  // fails to patch the DOM for these rows. `SortableTrackControls` already
  // sets `feedback: 'move'` to avoid the placeholder-splice half of this (see
  // its comment), but OptimisticSortingPlugin's own `reorder()` DOM splice
  // fires independently on any drag that commits a real index change and
  // isn't configurable away without losing live index tracking. Bumping this
  // epoch forces PlaylistVisualization to re-key (remount) the track-controls
  // slots after every track-reorder drag ends — committed or canceled, since
  // corruption can occur before a final index is even decided — discarding
  // any corrupted fiber state so the NEXT reorder (drag or button) starts
  // from a clean DOM. Called from ClipInteractionProvider's onDragEnd.
  const bumpTrackReorderEpoch = useCallback(() => {
    setTrackReorderEpoch((epoch) => epoch + 1);
  }, []);

  // Selection — wraps hook setter with playback side-effects (provider concern).
  const setSelection = useCallback(
    (start: number, end: number) => {
      setSelectionEngine(start, end);
      setCurrentTimeRefs(start);
      setCurrentTime(start);

      if (isPlaying && engineRef.current) {
        // Stop + seek before restarting playback from selection start.
        // engine.stop() resets _currentTime and _isPlaying, creating a window
        // where getCurrentTime() returns wrong value. seek() immediately
        // corrects _currentTime so any RAF firing in between reads the
        // intended position. engine.play() then restarts everything.
        // Suppressed: seek()+play() below immediately re-establish the correct
        // position — see suppressEngineStopMirrorRef declaration. Unguarded,
        // the mirror would also flip isPlaying to false with nothing here to
        // set it back true (isPlaying was already true on entry to this branch).
        suppressEngineStopMirrorRef.current = true;
        engineRef.current.stop();
        suppressEngineStopMirrorRef.current = false;
        engineRef.current.seek(start);
        engineRef.current.play(start);
      }
    },
    [isPlaying, setSelectionEngine, setCurrentTimeRefs]
  );

  // Memoize setScrollContainer callback
  const setScrollContainer = useCallback((element: HTMLDivElement | null) => {
    scrollContainerRef.current = element;
  }, []);

  // Stable callback ref for onAnnotationsChange to avoid re-creating controls context
  const onAnnotationsChangeRef = useRef(onAnnotationsChange);
  onAnnotationsChangeRef.current = onAnnotationsChange;

  const setAnnotations: React.Dispatch<React.SetStateAction<AnnotationData[]>> = useCallback(
    (action) => {
      const updated = typeof action === 'function' ? action(annotationsRef.current) : action;
      if (!onAnnotationsChangeRef.current) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            'waveform-playlist: setAnnotations was called but no onAnnotationsChange callback is provided. ' +
              'Annotation edits will not persist. Pass onAnnotationsChange to WaveformPlaylistProvider to handle annotation updates.'
          );
        }
        return;
      }
      onAnnotationsChangeRef.current(updated);
    },
    []
  );

  const sampleRate = sampleRateRef.current;
  const timeScaleHeight = timescale ? 30 : 0;
  const minimumPlaylistHeight = tracks.length * waveHeight + timeScaleHeight;

  // Split context values for performance optimization
  // Animation context only re-renders consumers on discrete events
  // (play/pause/stop/seek), never during the animation loop itself

  const animationValue: PlaybackAnimationContextValue = useMemo(
    () => ({
      isPlaying,
      currentTime,
      currentTimeRef,
      visualTimeRef,
      playbackStartTimeRef,
      audioStartPositionRef,
      getPlaybackTime,
      getAudioContextTime,
      getLookAhead,
      getOutputLatency,
      registerFrameCallback,
      unregisterFrameCallback,
    }),
    [
      isPlaying,
      currentTime,
      currentTimeRef,
      visualTimeRef,
      playbackStartTimeRef,
      audioStartPositionRef,
      getPlaybackTime,
      getAudioContextTime,
      getLookAhead,
      getOutputLatency,
      registerFrameCallback,
      unregisterFrameCallback,
    ]
  );

  const stateValue: PlaylistStateContextValue = useMemo(
    () => ({
      continuousPlay,
      linkEndpoints,
      annotationsEditable,
      isAutomaticScroll,
      isLoopEnabled,
      annotations,
      activeAnnotationId,
      selectionStart,
      selectionEnd,
      selectedTrackId,
      loopStart,
      loopEnd,
      indefinitePlayback,
      fillViewport,
      canUndo,
      canRedo,
    }),
    [
      continuousPlay,
      linkEndpoints,
      annotationsEditable,
      isAutomaticScroll,
      isLoopEnabled,
      annotations,
      activeAnnotationId,
      selectionStart,
      selectionEnd,
      selectedTrackId,
      loopStart,
      loopEnd,
      indefinitePlayback,
      fillViewport,
      canUndo,
      canRedo,
    ]
  );

  const setCurrentTimeControl = useCallback(
    (time: number) => {
      setCurrentTimeRefs(time);
      setCurrentTime(time);
    },
    [setCurrentTimeRefs]
  );

  const setAutomaticScrollControl = useCallback((enabled: boolean) => {
    setIsAutomaticScroll(enabled);
  }, []);

  const controlsValue: PlaylistControlsContextValue = useMemo(
    () => ({
      // Playback controls
      play,
      pause,
      stop,
      seekTo,
      setCurrentTime: setCurrentTimeControl,

      // Track controls
      setTrackMute,
      setTrackSolo,
      setTrackVolume,
      setTrackPan,
      reorderTrack,
      bumpTrackReorderEpoch,
      setTrackDragPreview,

      // Selection
      setSelection,
      setSelectedTrackId: setSelectedTrackIdControl,

      // Time format
      setTimeFormat,
      formatTime,

      // Zoom
      zoomIn: zoom.zoomIn,
      zoomOut: zoom.zoomOut,

      // Master volume
      setMasterVolume,

      // Automatic scroll
      setAutomaticScroll: setAutomaticScrollControl,
      setScrollContainer,
      scrollContainerRef,

      // Annotation controls
      setContinuousPlay,
      setLinkEndpoints,
      setAnnotationsEditable,
      setAnnotations,
      setActiveAnnotationId,

      // Loop controls
      setLoopEnabled,
      setLoopRegion,
      setLoopRegionFromSelection,
      clearLoopRegion,

      // Undo/redo
      undo,
      redo,

      // Recording
      setRecordingActive,
    }),
    [
      play,
      pause,
      stop,
      seekTo,
      setCurrentTimeControl,
      setTrackMute,
      setTrackSolo,
      setTrackVolume,
      setTrackPan,
      reorderTrack,
      bumpTrackReorderEpoch,
      setTrackDragPreview,
      setSelection,
      setSelectedTrackIdControl,
      setTimeFormat,
      formatTime,
      zoom.zoomIn,
      zoom.zoomOut,
      setMasterVolume,
      setAutomaticScrollControl,
      setScrollContainer,
      scrollContainerRef,
      setContinuousPlay,
      setLinkEndpoints,
      setAnnotationsEditable,
      setAnnotations,
      setActiveAnnotationId,
      setLoopEnabled,
      setLoopRegion,
      setLoopRegionFromSelection,
      clearLoopRegion,
      undo,
      redo,
      setRecordingActive,
    ]
  );

  const dataValue: PlaylistDataContextValue = useMemo(
    () => ({
      duration,
      audioBuffers,
      peaksDataArray,
      trackStates,
      tracks,
      sampleRate,
      waveHeight,
      timeScaleHeight,
      minimumPlaylistHeight,
      controls,
      playoutRef: engineRef,
      samplesPerPixel,
      timeFormat,
      masterVolume,
      canZoomIn: zoom.canZoomIn,
      canZoomOut: zoom.canZoomOut,
      barWidth,
      barGap,
      roundedBars,
      progressBarWidth,
      isReady,
      trackReorderEpoch,
      trackDragPreview,
      mono,
      isDraggingRef,
      onTracksChange,
    }),
    [
      duration,
      audioBuffers,
      peaksDataArray,
      trackStates,
      tracks,
      sampleRate,
      waveHeight,
      timeScaleHeight,
      minimumPlaylistHeight,
      controls,
      engineRef,
      samplesPerPixel,
      timeFormat,
      masterVolume,
      zoom.canZoomIn,
      zoom.canZoomOut,
      barWidth,
      barGap,
      roundedBars,
      progressBarWidth,
      isReady,
      trackReorderEpoch,
      trackDragPreview,
      mono,
      isDraggingRef,
      onTracksChange,
    ]
  );

  // Merge user theme with default theme
  const mergedTheme = { ...defaultTheme, ...userTheme };

  return (
    <ThemeProvider theme={mergedTheme}>
      <PlaybackAnimationContext.Provider value={animationValue}>
        <PlaylistStateContext.Provider value={stateValue}>
          <PlaylistControlsContext.Provider value={controlsValue}>
            <PlaylistDataContext.Provider value={dataValue}>
              {children}
            </PlaylistDataContext.Provider>
          </PlaylistControlsContext.Provider>
        </PlaylistStateContext.Provider>
      </PlaybackAnimationContext.Provider>
    </ThemeProvider>
  );
};

// Individual hooks for each context - use these for optimal performance
// Components only re-render when their specific context data changes

export const usePlaybackAnimation = () => {
  const context = useContext(PlaybackAnimationContext);
  if (!context) {
    throw new Error('usePlaybackAnimation must be used within WaveformPlaylistProvider');
  }
  return context;
};

export const usePlaylistState = () => {
  const context = useContext(PlaylistStateContext);
  if (!context) {
    throw new Error('usePlaylistState must be used within WaveformPlaylistProvider');
  }
  return context;
};

export const usePlaylistControls = () => {
  const context = useContext(PlaylistControlsContext);
  if (!context) {
    throw new Error('usePlaylistControls must be used within WaveformPlaylistProvider');
  }
  return context;
};

export const usePlaylistData = () => {
  const context = useContext(PlaylistDataContext);
  if (!context) {
    throw new Error('usePlaylistData must be used within WaveformPlaylistProvider');
  }
  return context;
};

/**
 * Like {@link usePlaylistData} but returns `null` instead of throwing when there
 * is no WaveformPlaylistProvider ancestor. Use this in hooks/components that may
 * also run in the MediaElement path (MediaElementPlaylistProvider), where the
 * WebAudio playlist context is absent.
 */
export const usePlaylistDataOptional = () => useContext(PlaylistDataContext);
